export type MegaventoryWebhookReceiverSlug =
  | "document"
  | "product"
  | "sales_order"
  | "purchase_order"
  | "work_order"
  | "supplier"
  | "client";

/**
 * Megaventory HTTP callback wiring for one ingest entity (from `GET /api/ingest-control`).
 */
export interface MegaventoryWebhookSubscriptionLite {
  event_type: string;
  mv_event_id: number;
}

export interface MegaventoryWebhookDashboardSlice {
  applicable: boolean;
  receiver: MegaventoryWebhookReceiverSlug | null;
  subscriptions_registered: boolean;
  subscription_count: number;
  subscriptions: MegaventoryWebhookSubscriptionLite[];
  /**
   * Total EventSubscription rows stored for this receiver URL when it differs from `subscription_count`
   * (shared listener; UI lists only event types routed to this ingest entity).
   */
  receiver_subscription_count?: number;
  listener_url_masked: string | null;
  subscriptions_updated_at: string | null;
  last_webhook_run_at: string | null;
  /**
   * Megaventory DocumentTypeId values on the document webhook JSON that route callbacks to this ingest entity.
   * Omitted when this entity does not use the document listener.
   */
  document_type_ids?: readonly number[];
}

/**
 * Path segment for `POST` / `DELETE` `/api/megaventory/webhook-subscriptions/<segment>` (Megaventory EventSubscription ensure).
 * Matches `registerMegaventoryWebhookSubscriptionRoutes` in the API.
 */
export function megaventoryReceiverSubscriptionApiPath(receiver: MegaventoryWebhookReceiverSlug): string {
  switch (receiver) {
    case "document":
      return "document-gosi";
    case "product":
      return "product-item";
    case "sales_order":
      return "sales-order";
    case "purchase_order":
      return "purchase-order";
    case "work_order":
      return "work-order";
    case "supplier":
      return "supplier";
    case "client":
      return "client";
  }
}

/**
 * One row of GET /api/ingest-control (`select * from workflow.ingest_control`).
 */
export interface IngestControlEntity {
  entity: string;
  source: "n8n_only" | "app_only";
  enabled: boolean;
  schedule_cron: string | null;
  sweep_schedule_cron: string | null;
  watermark_safety_overlap_minutes: number;
  watermark_advance_lag_minutes: number;
  batch_size: number;
  max_records_per_run: number;
  retry_delay_seconds: number;
  staging_enabled: boolean;
  /**
   * When a live, replay, or staging run last finished. Dry runs, diff, and sweeps do not advance this
   * (use Recent runs, `last_diff_at`, and `last_sweep_at` / sweep summary instead).
   */
  last_run_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  last_diff_at: string | null;
  last_diff_summary: unknown;
  last_sweep_at: string | null;
  last_sweep_summary: unknown;
  notes: string | null;
  updated_at?: string;
  /**
   * From GET /api/ingest-control: unresolved `ingest_event` rows (failed, drift, missing, orphan).
   * Prefer this and Findings over `last_error` (deprecated column; not written by current ingests). Older APIs omit it.
   */
  open_problems_count?: number;
  /**
   * When false, `sweep_report` / `sweep_apply` are not offered (live ingest already replaces the full target set).
   * Older hub builds omit this field — treat as true.
   */
  sweep_report_supported?: boolean;
  megaventory_webhook?: MegaventoryWebhookDashboardSlice;
  /**
   * True when a Freshsales company or contact webhook replays this entity.
   * Scheduled Freshsales pulls (deals, notes) are false here.
   */
  freshsales_push_webhook?: boolean;
}

export type IngestSourceFamily = "megaventory" | "freshsales" | "other";

const FRESHSALES_SCHEDULED_ENTITIES = new Set([
  "freshsales_contact",
  "freshsales_deal",
  "freshsales_deal_note",
  "freshsales_account_note",
  "freshsales_note",
  "rep_staff",
]);

/** Which upstream system owns the scheduled pull for this ingest_control key. */
export function ingestSourceFamily(entity: string): IngestSourceFamily {
  if (entity.startsWith("freshsales_") || FRESHSALES_SCHEDULED_ENTITIES.has(entity)) {
    return "freshsales";
  }
  if (entity === "shipping_provider") {
    return "other";
  }
  return "megaventory";
}

const FRESHSALES_PUSH_WEBHOOK_ENTITIES = new Set(["customer", "freshsales_contact", "rep_staff"]);

export function freshsalesPushWebhookApplies(entity: string): boolean {
  return FRESHSALES_PUSH_WEBHOOK_ENTITIES.has(entity);
}

/**
 * Response shape for GET /api/ingest-control.
 * `scheduler` is present in current API builds; older servers omit it.
 */
export interface SchedulerLiveness {
  lastSeenAt: string | null;
  ok: boolean;
  expectedTickSeconds: number;
}

export const DEFAULT_SCHEDULER_LIVENESS: SchedulerLiveness = {
  lastSeenAt: null,
  ok: false,
  expectedTickSeconds: 30,
};

export interface IngestControlListResponse {
  entities: IngestControlEntity[];
  /** True when the API host has `INTEGRATOR_MEGAVENTORY_WEBHOOK_SECRET` set so `/api/webhooks/megaventory/*` routes are active. */
  megaventory_webhook_secret_configured?: boolean;
  /** True when INTEGRATOR_FRESHSALES_WEBHOOK_SECRET is set so /api/webhooks/freshsales/entity is registered. */
  freshsales_webhook_secret_configured?: boolean;
  scheduler?: SchedulerLiveness;
  /**
   * Entity keys with a registered TypeScript ingest module (`src/ingest/register-all`).
   * Rows may still be `n8n_only` in the database; this only reflects code availability.
   */
  registered_entities?: string[];
}

/**
 * Use with `useQuery` for `GET /api/ingest-control` so `last_run_at` / `last_success_at` stay in sync
 * with completed runs. The global query client uses `staleTime: 5_000` and `refetchOnWindowFocus: false`,
 * which otherwise left those columns stale on the dashboard after a run finished or after route changes.
 */
export const INGEST_CONTROL_QUERY_LIVE = {
  staleTime: 0,
  refetchOnMount: "always" as const,
};
